// @ts-nocheck

import React, { useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { clearSearch } from '../../actions/search';
import { clearPreferences } from '../../actions/user';
import useMergeProps from '../../hooks/useMergeProps';
import useRouter from '../../hooks/useRouter';
import useSportsMenuScrollContext from '../../hooks/useSportsMenuScrollContext';
import useOpenBoomView from '../../modules/boom/hooks/useOpenBoomView';
import useOpenGRView from '../../modules/gift-race/hooks/useOpenGRView';
import { openKLView } from '../../modules/koh-lanta/actions/routes';
import { requestRefreshUserMissionsState } from '../../modules/missions/actions/userMissions';
import { LocalStorageMissionsKeys } from '../../modules/missions/constants/storage';
import { openMPView } from '../../modules/monopoly/actions/routes';
import { BetslipFilters } from '../../types/history';
import { TabBarNavigationTypes, type TabBarNavigationType } from '../../types/tabBar';
import { storage } from '../../utils';
import RoutesRedirectionFactory from '../../utils/routes/RoutesRedirectionFactory';

import NavigationTabBarItem from './NavigationTabBarItem';
import makeNavigationTabBarItemPropsSelector from './NavigationTabBarItem.selector';

const emptyArr = [];

type NavigationTabBarItemContainerProps = {
  id: TabBarNavigationType;
  className?: string;
};

const NavigationTabBarItemContainer = (ownProps: NavigationTabBarItemContainerProps) => {
  const { id: tabBarItemId } = ownProps;
  const {
    history,
    location: { pathname },
  } = useRouter();
  const dispatch = useDispatch();
  const historyRef = useRef(history);
  const dispatchRef = useRef(dispatch);
  const seenMissions = storage.localStorage.load(LocalStorageMissionsKeys.SEEN_MISSIONS) ?? emptyArr;
  const openGRView = useOpenGRView();
  const openBoomView = useOpenBoomView();

  const selectorProps = useMemo(
    () => ({ tabBarItemId, pathname, seenMissions }),
    [tabBarItemId, pathname, seenMissions],
  );
  const selector = useMemo(() => makeNavigationTabBarItemPropsSelector(), []);
  const selectedProps = useSelector((state) => selector(state, selectorProps));

  const { setScrollPositions } = useSportsMenuScrollContext();

  const onClick = useCallback(() => {
    const dispatchCurrent = dispatchRef.current;
    const historyCurrent = historyRef.current;

    if (tabBarItemId === TabBarNavigationTypes.HOME) {
      historyCurrent.push(RoutesRedirectionFactory.getHomeRouteConfig());
    } else if (tabBarItemId === TabBarNavigationTypes.SPORTS) {
      setScrollPositions({});
      dispatchCurrent(clearSearch());
      historyCurrent.push(RoutesRedirectionFactory.getSportsMenuRouteConfig());
    } else if (tabBarItemId === TabBarNavigationTypes.LIVE) {
      dispatchCurrent(clearPreferences('live'));
      historyCurrent.push(RoutesRedirectionFactory.getLiveRouteConfig());
    } else if (tabBarItemId === TabBarNavigationTypes.BASKET) {
      historyCurrent.push(RoutesRedirectionFactory.getBasketRouteConfig());
    } else if (tabBarItemId === TabBarNavigationTypes.MY_BETS) {
      historyCurrent.push(RoutesRedirectionFactory.getHistoryRouteConfig(BetslipFilters.RUNNING));
    } else if (tabBarItemId === TabBarNavigationTypes.GRIDS) {
      historyCurrent.push(RoutesRedirectionFactory.getGridsRouteConfig());
    } else if (tabBarItemId === TabBarNavigationTypes.GRIDS_HISTORY) {
      historyCurrent.push(RoutesRedirectionFactory.getGridsHistoryRouteConfig());
    } else if (tabBarItemId === TabBarNavigationTypes.KOH_LANTA) {
      dispatchCurrent(openKLView());
    } else if (tabBarItemId === TabBarNavigationTypes.GIFT_RACE) {
      openGRView();
    } else if (tabBarItemId === TabBarNavigationTypes.MONOPOLY) {
      dispatchCurrent(openMPView());
    } else if (tabBarItemId === TabBarNavigationTypes.BOOM) {
      if (pathname !== RoutesRedirectionFactory.getBoomEnrollementRoutePathname()) {
        openBoomView();
      }
    } else if (tabBarItemId === TabBarNavigationTypes.EXTRAS) {
      dispatchCurrent(requestRefreshUserMissionsState());
      historyCurrent.push(RoutesRedirectionFactory.getExtrasRouteConfig());
    }
  }, [tabBarItemId, setScrollPositions, openGRView, pathname, openBoomView]);

  const otherProps = useMemo(() => ({ onClick }), [onClick]);

  const enhancedProps = useMergeProps({ ownProps, selectedProps, otherProps });

  return <NavigationTabBarItem {...enhancedProps} />;
};

export default NavigationTabBarItemContainer;
